import { Appliance, Group, Hood } from '@/types/helpers';
import { ApplianceId, RoomId } from '@/api/airchef/Api';
import { InvalidStateError } from '@/common/errors';
import { scaleAssetForStore } from '@/common/planningUtils';
import { GroupUpdater } from '@/common/itemUtils';
import { convertMeasurement } from '@/common/measurements';
import { PlanSlice } from '@/types/stores/plan';
import { ProjectSlice } from '@/types/stores/project';
import { planAction } from '../storeCommon';
import { simpleClone } from '@/common/utils';

/**
 * Normalizes appliance input and returns scaled asset
 */
export function getApplianceAsset(
  appliance: Appliance | ApplianceId,
  getAsset: PlanSlice['plan']['getAsset'],
): Appliance {
  const applianceId = typeof appliance === 'string' ? appliance : appliance.id;
  const applianceAsset = scaleAssetForStore(
    typeof appliance === 'string' ? simpleClone(getAsset(applianceId)!) : appliance,
  ) as Appliance;

  return applianceAsset;
}

/**
 * Gets existing group or creates a new one for the appliance
 */
export async function getOrCreateGroup(
  applianceAsset: Appliance,
  availableGroupGet: () => Promise<Group>,
  projectGroups: Record<string, Group>,
): Promise<{ group: Group; isNewGroup: boolean }> {
  const groupId = applianceAsset.relations.groupId;

  if (groupId && projectGroups[groupId]) {
    return {
      group: simpleClone(projectGroups[groupId]),
      isNewGroup: false,
    };
  }

  const baseGroup = await availableGroupGet();
  const applianceId = applianceAsset.id;

  const group = new GroupUpdater(baseGroup)
    .setGenerateId(true)
    .setRow(0, [applianceId])
    .setRoomId(applianceAsset.relations.roomId!)
    .setPosition(
      applianceAsset.airchefValues.offsetTransform?.position ?? applianceAsset.airchefValues.transform.position,
    )
    .setRotation(applianceAsset.airchefValues.transform.rotation.x)
    .update();

  return { group, isNewGroup: true };
}

/**
 * Updates appliance relations for a newly created group
 */
export function updateApplianceForNewGroup(applianceAsset: Appliance, group: Group): void {
  applianceAsset.relations.rowNum = 0;
  applianceAsset.relations.rowId = group.rows[0].id;
}

/**
 * Determines the position and angle for the hood based on the appliance or first appliance in row
 */
export function determineHoodPosition(
  applianceAsset: Appliance,
  group: Group,
  rowNum: 0 | 1,
  getAsset: ProjectSlice['project']['getAsset'],
): { xPos: number; yPos: number; angle: number } {
  const row = group.rows[rowNum];
  if (!row) {
    throw new InvalidStateError(`Could not get row (${rowNum}) from group ${group.id}`);
  }

  // Default to current appliance's position (in mm)
  let xPos = applianceAsset.airchefValues.transform.position.x.value;
  let yPos = applianceAsset.airchefValues.transform.position.y.value;
  let angle = applianceAsset.airchefValues.transform.rotation.x;

  // If this appliance is not the first one in the row, use the first appliance's position
  if (row.relations.applianceIds[0] !== applianceAsset.id) {
    const firstAppliance = getAsset(row.relations.applianceIds[0]);
    if (firstAppliance) {
      xPos = convertMeasurement(firstAppliance.airchefValues.transform.position.x, 'mm').value;
      yPos = convertMeasurement(firstAppliance.airchefValues.transform.position.y, 'mm').value;
      angle = firstAppliance.airchefValues.transform.rotation.x;
    }
  }

  return { xPos, yPos, angle };
}

/**
 * Creates a hood instance at the specified position
 */
export function createHoodInstance(
  hoodHaltonId: string | number,
  roomId: RoomId,
  position: { xPos: number; yPos: number; angle: number },
  createAsset: ProjectSlice['project']['createAsset'],
): Hood {
  const hoodInstance = simpleClone(
    createAsset(
      {
        roomId,
        type: 'hood',
        itemId: hoodHaltonId,
        x: position.xPos,
        y: position.yPos,
        inRoom: true,
        angle: position.angle,
      },
      planAction,
      'createHoodAtAppliance',
    ),
  ) as Hood;

  if (!hoodInstance) {
    throw new InvalidStateError('Could not create hood');
  }

  return hoodInstance;
}

/**
 * Updates all relations between group, hood, and appliance
 */
export function updateRelations(
  group: Group,
  hoodInstance: Hood,
  applianceAsset: Appliance,
  rowNum: 0 | 1,
): { updatedGroup: Group; updatedHood: Hood; updatedAppliance: Appliance } {
  const updatedGroup = new GroupUpdater(group).setRow(rowNum, undefined, [hoodInstance.id]).update();

  if (!updatedGroup.rows[rowNum]) {
    throw new InvalidStateError(`Group is missing row (${rowNum}) while adding hood`);
  }

  const updatedHood = hoodInstance;
  updatedHood.relations.groupId = updatedGroup.id;
  updatedHood.relations.rowNum = rowNum;
  updatedHood.relations.rowId = updatedGroup.rows[rowNum].id;

  const updatedAppliance = applianceAsset;
  updatedAppliance.relations.groupId = updatedGroup.id;
  updatedAppliance.relations.rowNum = rowNum;
  updatedAppliance.relations.rowId = updatedGroup.rows[rowNum].id;

  return { updatedGroup, updatedHood, updatedAppliance };
}

/**
 * Main function to create a hood at an appliance location
 */
export async function createHoodAtAppliance(
  appliance: Appliance | ApplianceId,
  hood: Hood | string | number,
  planSlice: PlanSlice['plan'],
  projectSlice: ProjectSlice['project'],
  getState: () => { project: ProjectSlice['project']; plan: PlanSlice['plan'] },
): Promise<Hood | null> {
  const { plan, project } = { plan: planSlice, project: projectSlice };

  // Step 1: Get and validate appliance
  const applianceAsset = getApplianceAsset(appliance, plan.getAsset);

  if (!applianceAsset.relations.roomId) {
    console.error('Can not group appliances not in room');
    return null;
  }

  // Step 2: Get or create group
  const { group, isNewGroup } = await getOrCreateGroup(applianceAsset, plan.availableGroup.get, project.data.groups);

  // Step 3: Validate group doesn't already have hoods
  if (group.relations.hoodIds.length > 0) {
    console.error('Can not create a hood in group that already has a hood!');
    return null;
  }

  // Step 4: Update appliance relations if it's a new group
  if (isNewGroup) {
    updateApplianceForNewGroup(applianceAsset, group);
  }

  // Step 5: Extract hood ID
  const hoodHaltonId = typeof hood === 'number' || typeof hood === 'string' ? hood : hood.haltonValues.id;

  plan.startBatchAction('createHoodAtAppliance');

  try {
    // Step 6: Determine hood position
    const rowNum = (applianceAsset.relations.rowNum ?? 0) as 0 | 1;
    const position = determineHoodPosition(applianceAsset, group, rowNum, project.getAsset);

    // Step 7: Create hood instance
    const hoodInstance = createHoodInstance(
      hoodHaltonId,
      applianceAsset.relations.roomId,
      position,
      project.createAsset,
    );

    // Step 8: Update all relations
    const { updatedGroup, updatedHood, updatedAppliance } = updateRelations(
      group,
      hoodInstance,
      applianceAsset,
      rowNum,
    );

    // Step 9: Persist updates
    project.updateGroup(updatedGroup, planAction, 'createHoodAtAppliance');
    project.updateAssets([updatedAppliance, updatedHood], planAction, 'createHoodAtAppliance');

    // Step 10: Wait for effects to run
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Step 11: Return final hood from state
    const finalHood = getState().project.data.hoods[hoodInstance.id];
    if (finalHood.type === 'hood') {
      return finalHood;
    }
    return null;
  } catch (err) {
    console.error(err);
    return null;
  } finally {
    plan.endBatchAction('createHoodAtAppliance');
  }
}
